import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Drawer from '../../Drawer';
import { AgentContext } from '../../../types';
import { HistoryData, useChatHistory } from '../hooks/useChatHistory';
import { chatBackground } from '../styles';
import { ToolRendererFunction } from '../types';
import AiChat from './AiChat';
import AiChatTitle from './AiChatTitle';
import { ChatSettings } from './ChatSettings';

const DrawerStyled = styled(Drawer)`
  .drawer-wrapper {
    ${chatBackground}
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  dragHandleOffset?: { x: number; y: number };
  suggestions?: string[];
  placeholder?: string;
  startMessage?: string;
  title: string;
  url: string;
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  onBack?: () => void;
  backTooltip?: string;
  context?: AgentContext;
};

const AiChatDrawer = ({
  open,
  onClose,
  dragHandleOffset,
  suggestions,
  placeholder,
  startMessage,
  title,
  url,
  getToolRenderer,
  onBack,
  backTooltip,
  context,
}: Props) => {
  const [chatComponentKey, setChatComponentKey] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  const { history, resetChatHistory, handleSelectHistory } = useChatHistory({
    open,
  });

  const handleNewChatClick = () => {
    setChatComponentKey((prevKey) => prevKey + 1);
    resetChatHistory();
    setShowSettings(false);
  };

  const handleSelectHistoryClick = (history: HistoryData) => {
    handleSelectHistory(history);
    setShowSettings(false);
  };

  const isStartMessageEnabled = chatComponentKey === 0;

  // rendering conditionally with "open" to make sure start message is triggered when drawer stays mounted when closed
  const showChat = open && !showSettings;

  // resetting chat key to 0 on close, for instances when the drawer stays mounted when closed
  useEffect(() => {
    if (!open) {
      setChatComponentKey(0);
      setShowSettings(false);
    }
  }, [open]);

  return (
    <DrawerStyled
      title={
        <AiChatTitle
          title={title}
          onNewChat={handleNewChatClick}
          onSelectHistory={handleSelectHistoryClick}
          onOpenSettings={() => setShowSettings(true)}
          onBack={onBack}
          backTooltip={backTooltip}
        />
      }
      isOpen={open}
      onClose={onClose}
      dragHandleOffset={dragHandleOffset}
      noPadding
    >
      {showSettings && <ChatSettings chatName={title} />}
      {showChat && (
        <AiChat
          key={chatComponentKey}
          chatEndpoint={url}
          title={placeholder}
          placeholder={placeholder}
          suggestions={suggestions}
          startMessage={isStartMessageEnabled ? startMessage : undefined}
          chatHistory={history}
          getToolRenderer={getToolRenderer}
          context={context}
        />
      )}
    </DrawerStyled>
  );
};

export default AiChatDrawer;
